// kentico cloud
import LanguageService from './Language.service';
import { DeliveryClient, TypeResolver } from 'kentico-cloud-delivery';
import {
  KENTICO_TYPES,
  PREVIEW_API_KEY,
  PROJECT_ID
} from 'common/consts/kentico';
import {
  Award,
  Box,
  CaseStudy,
  ChartElement,
  Comment,
  Company,
  CompanyLogo,
  CompanyInfoBox,
  Content,
  Country,
  Enterprise,
  Event,
  File,
  History,
  JobOffer,
  Link,
  Management,
  MissionAndVisionBigBox,
  News,
  Office,
  Partner,
  Person,
  Article,
  ArticleNews,
  Shareholders,
  Product,
  Panel,
  Report,
  Report2,
  Report3,
  RiskAndThreat,
  SourceOfIncome,
  Service,
  Specialization,
  SimpleCaseStudy,
  VideoComment,
  TeamOfExperts,
  TwoColumnsPanel,
  FinancialResults,
  SEO,
  ForInvestors,
  ForInvestorsNews,
  ForInvestorsUpcomingEvents,
  ForInvestorsEvent,
  JobOfferLandingPage,
  JobOfferPageContent,
  GlobalMetaTags,
  GlobalMetaTag,
  IssueOfShares,
  Presentation
} from 'models';

// configure type resolvers
const typeResolvers = [
  new TypeResolver(KENTICO_TYPES.AWARD, () => new Award()),
  new TypeResolver(KENTICO_TYPES.BOX, () => new Box()),
  new TypeResolver(KENTICO_TYPES.CASE_STUDY, () => new CaseStudy()),
  new TypeResolver(KENTICO_TYPES.COMMENT, () => new Comment()),
  new TypeResolver(KENTICO_TYPES.CHART_ELEMENT, () => new ChartElement()),
  new TypeResolver(KENTICO_TYPES.COUNTRY, () => new Country()),
  new TypeResolver(KENTICO_TYPES.ENTERPRISE, () => new Enterprise()),
  new TypeResolver(KENTICO_TYPES.COMPANY, () => new Company()),
  new TypeResolver(KENTICO_TYPES.COMPANY_LOGO, () => new CompanyLogo()),
  new TypeResolver(KENTICO_TYPES.COMPANY_INFO_BOX, () => new CompanyInfoBox()),
  new TypeResolver(KENTICO_TYPES.CONTENT, () => new Content()),
  new TypeResolver(KENTICO_TYPES.EVENT, () => new Event()),
  new TypeResolver(KENTICO_TYPES.FILE, () => new File()),
  new TypeResolver(KENTICO_TYPES.HISTORY, () => new History()),
  new TypeResolver(KENTICO_TYPES.JOB_OFFER, () => new JobOffer()),
  new TypeResolver(KENTICO_TYPES.LINK, () => new Link()),
  new TypeResolver(KENTICO_TYPES.MANAGEMENT, () => new Management()),
  new TypeResolver(
    KENTICO_TYPES.MISSION_AND_VISION_BIG_BOX,
    () => new MissionAndVisionBigBox()
  ),
  new TypeResolver(KENTICO_TYPES.NEWS, () => new News()),
  new TypeResolver(KENTICO_TYPES.OFFICE, () => new Office()),
  new TypeResolver(KENTICO_TYPES.PARTNER, () => new Partner()),
  new TypeResolver(KENTICO_TYPES.PERSON, () => new Person()),
  new TypeResolver(KENTICO_TYPES.BOARD_OF_DIRECTORS, () => new Person()),
  new TypeResolver(KENTICO_TYPES.PRESS_ARTICLE, () => new Article()),
  new TypeResolver(KENTICO_TYPES.PRESS_ROOM_NEWS, () => new ArticleNews()),
  new TypeResolver(KENTICO_TYPES.PRODUCT, () => new Product()),
  new TypeResolver(KENTICO_TYPES.PANEL, () => new Panel()),
  new TypeResolver(KENTICO_TYPES.REPORT, () => new Report()),
  new TypeResolver(KENTICO_TYPES.REPORT2, () => new Report2()),
  new TypeResolver(KENTICO_TYPES.REPORT3, () => new Report3()),
  new TypeResolver(KENTICO_TYPES.RISK_AND_THREAT, () => new RiskAndThreat()),
  new TypeResolver(KENTICO_TYPES.SERVICE, () => new Service()),
  new TypeResolver(KENTICO_TYPES.SOURCE_OF_INCOME, () => new SourceOfIncome()),
  new TypeResolver(KENTICO_TYPES.SPECIALIZATION, () => new Specialization()),
  new TypeResolver(
    KENTICO_TYPES.SIMPLE_CASE_STUDY,
    () => new SimpleCaseStudy()
  ),
  new TypeResolver(KENTICO_TYPES.TEAM_OF_EXPERTS, () => new TeamOfExperts()),
  new TypeResolver(
    KENTICO_TYPES.TWO_COLUMNS_PANEL,
    () => new TwoColumnsPanel()
  ),
  new TypeResolver(
    KENTICO_TYPES.SHAREHOLDERS_STRUCTURE,
    () => new Shareholders()
  ),
  new TypeResolver(KENTICO_TYPES.VIDEO_COMMENT, () => new VideoComment()),
  new TypeResolver(KENTICO_TYPES.SEO, () => new SEO()),
  new TypeResolver(KENTICO_TYPES.FOR_INVESTORS, () => new ForInvestors()),
  new TypeResolver(
    KENTICO_TYPES.FOR_INVESTORS_NEWS,
    () => new ForInvestorsNews()
  ),
  new TypeResolver(
    KENTICO_TYPES.FOR_INVESTORS_UPCOMING_EVENTS,
    () => new ForInvestorsUpcomingEvents()
  ),
  new TypeResolver(
    KENTICO_TYPES.FINANCIAL_RESULTS,
    () => new FinancialResults()
  ),
  new TypeResolver(
    KENTICO_TYPES.FOR_INVESTORS_EVENT,
    () => new ForInvestorsEvent()
  ),
  new TypeResolver(KENTICO_TYPES.CURRENT_REPORTS, () => new ArticleNews()),
  new TypeResolver(KENTICO_TYPES.CALENDARIUM, () => new ArticleNews()),
  new TypeResolver(KENTICO_TYPES.GENERAL_MEETINGS, () => new ArticleNews()),
  new TypeResolver(KENTICO_TYPES.PERIODIC_REPORTS, () => new ArticleNews()),
  new TypeResolver(
    KENTICO_TYPES.LANDING_PAGE_JOB_OFFER,
    () => new JobOfferLandingPage()
  ),
  new TypeResolver(
    KENTICO_TYPES.JOB_OFFER_CONTENT,
    () => new JobOfferPageContent()
  ),
  new TypeResolver(KENTICO_TYPES.GLOBAL_META_TAGS, () => new GlobalMetaTags()),
  new TypeResolver(KENTICO_TYPES.GLOBAL_META_TAG, () => new GlobalMetaTag()),
  new TypeResolver(KENTICO_TYPES.ISSUE_OF_SHARES, () => new IssueOfShares()),

  new TypeResolver(KENTICO_TYPES.PRESENTATIONS, () => new Presentation())
];

const isPreview = () => PREVIEW_API_KEY !== '';
if (isPreview()) {
  console.info('PREVIEW MODE IS ENABLED');
}

const lang = LanguageService.getLang();

const Client = new DeliveryClient({
  projectId: PROJECT_ID,
  typeResolvers: [...typeResolvers],
  enablePreviewMode: isPreview(),
  previewApiKey: PREVIEW_API_KEY,
  isDeveloperMode: true,
  //stays temporarily for any edge case
  // defaultLanguage: lang,
  modularContentResolver: {
    modularContentWrapperTag: 'div'
  },
  globalQueryConfig: {
    usePreviewMode: isPreview()
  }
});

const _items = Client.items.bind(Client);
Client.items = () => {
  const query = _items();
  query.equalsFilter('system.language', lang);
  query.equalsFilter('system.workflow_step[neq]', 'archived');
  query.languageParameter(lang);
  return query;
};
Client.kenticoItems = () => _items();

export default Client;



// WEBPACK FOOTER //
// ./src/services/kenticoClient.js